/**
 * DailyDPRCapture.jsx — mobile-first Daily Progress Report screen.
 *
 * Built for site supervisors:
 *   - Data source is strictly aligned with the desktop Activity Register
 *     (GET /v1/projects/{pid}/fc/activities?limit=500) — no client-side
 *     filter drops rows; COMPLETED activities render as read-only.
 *   - Auto-adapts to platform theme (light / dark) via ThemeContext so the
 *     mobile screen matches whatever the desktop is on.
 *   - Large 60px+ touch targets, sticky top bar + save bar, discipline
 *     chips with the exact colour palette used by the desktop Register.
 *   - Ultra-interactive: search, quick-fill chips (Match Plan · +25% · 100%),
 *     per-card progress bar mirroring the desktop Wt% cell, running
 *     total footer, voice-to-text remarks, photo evidence with offline
 *     queue.
 *
 * Route: /project-controls/construction/dpr
 *
 * Backend: PUT /api/v1/projects/{pid}/fc/activities/{id}/progress/{YYYY-MM-DD}
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { dprQueue } from '@/lib/dprOfflineQueue';
import { useProject } from '@/contexts/ProjectContext';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import {
  ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, CloudOff,
  CloudUpload, Mic, MicOff, Save, CheckCircle2, AlertCircle, Search,
  Loader2, RefreshCw, Wifi, WifiOff, Camera, X, ImagePlus, Sparkles,
  Lock, TrendingUp, HardHat,
} from 'lucide-react';
import { SiteReturnSheet } from '@/components/sitereturns/SiteReturnSheet';

const STATUS_COLOR = {
  NOT_STARTED: '#64748b',
  ON_TRACK:    '#10b981',
  AHEAD:       '#06b6d4',
  BEHIND:      '#ef4444',
  COMPLETED:   '#22c55e',
  SUSPENDED:   '#f59e0b',
};
const STATUS_LABEL = {
  NOT_STARTED: 'Not started',
  ON_TRACK:    'On track',
  AHEAD:       'Ahead',
  BEHIND:      'Behind',
  COMPLETED:   'Completed',
  SUSPENDED:   'Suspended',
};

function fmtDate(d) { return d.toISOString().slice(0, 10); }
function fmtLabel(d) {
  return d.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' });
}
function fmtLong(d) {
  return d.toLocaleDateString(undefined, { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
}

// Light-first palette — auto-swaps to dark when platform theme toggles.
function usePalette() {
  const { isDark } = useTheme();
  return useMemo(() => (isDark ? {
    pageBg:    '#0a0e14',
    surface:   '#0f1722',
    surfaceAlt:'#111827',
    inset:     '#0a0e14',
    border:    '#1f2937',
    borderSoft:'#111827',
    text:      '#e5e7eb',
    textMuted: '#9ca3af',
    textFaint: '#6b7280',
    accent:    '#3b82f6',
    accentSoft:'#3b82f620',
    barTrack:  '#111827',
    stickyBg:  'rgba(10,14,20,0.94)',
    completedBg: '#16a34a15',
    inputColorScheme: 'dark',
  } : {
    pageBg:    '#f6f8fb',
    surface:   '#ffffff',
    surfaceAlt:'#ffffff',
    inset:     '#f4f6fa',
    border:    '#e5e7eb',
    borderSoft:'#eef1f5',
    text:      '#0f172a',
    textMuted: '#475569',
    textFaint: '#94a3b8',
    accent:    '#2563eb',
    accentSoft:'#2563eb14',
    barTrack:  '#eef1f5',
    stickyBg:  'rgba(246,248,251,0.94)',
    completedBg: '#dcfce7',
    inputColorScheme: 'light',
  }), [isDark]);
}

export default function DailyDPRCapture() {
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const projectId = selectedProject?.project_id;
  const [params] = useSearchParams();
  const initialDate = params.get('d') || fmtDate(new Date());
  const P = usePalette();

  const [date, setDate] = useState(initialDate);
  const [activities, setActivities] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [discFilter, setDiscFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [returnFor, setReturnFor] = useState(null);
  const [entries, setEntries] = useState({});           // {actId: {qty, remarks, dirty, queuedId}}
  const [online, setOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);
  const [voiceFor, setVoiceFor] = useState(null);       // actId of card currently capturing voice

  // ── Connectivity & queue listener ───────────────────────────────────────
  useEffect(() => {
    const onUp = () => setOnline(true);
    const onDown = () => setOnline(false);
    window.addEventListener('online', onUp);
    window.addEventListener('offline', onDown);
    return () => {
      window.removeEventListener('online', onUp);
      window.removeEventListener('offline', onDown);
    };
  }, []);

  const refreshQueueCount = useCallback(async () => {
    try {
      const items = await dprQueue.peekAll();
      setQueueCount(items.length);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    refreshQueueCount();
    const cb = () => refreshQueueCount();
    dprQueue.on('change', cb);
    return () => dprQueue.off('change', cb);
  }, [refreshQueueCount]);

  useEffect(() => {
    if (!online) return;
    (async () => {
      const items = await dprQueue.peekAll();
      if (!items.length) return;
      const res = await dprQueue.sync(api);
      if (res.synced > 0) toast.success(`Synced ${res.synced} queued daily update${res.synced > 1 ? 's' : ''}`);
      if (res.failed > 0) toast.error(`${res.failed} update${res.failed > 1 ? 's' : ''} still pending — will retry`);
      refreshQueueCount();
    })();
  }, [online, refreshQueueCount]);

  // ── Data fetch — SAME endpoint as desktop Activity Register ─────────────
  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [actsRes, discRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/fc/activities?limit=500${discFilter ? `&discipline=${discFilter}` : ''}`),
        api.get(`/v1/projects/${projectId}/fc/disciplines`),
      ]);
      // NO client-side status filter — the desktop Register shows every
      // active activity (incl. COMPLETED), so the DPR mobile view must too.
      const acts = (actsRes.data?.activities || []).filter(a => a.isActive !== false);
      setActivities(acts);
      setDisciplines(discRes.data || []);
      const init = {};
      acts.forEach(a => {
        const actual = (a.actualProgress || []).find(p => p.periodId === date);
        init[a.id] = {
          qty:      actual?.mtdActualQty ?? '',
          remarks:  actual?.remarks || '',
          dirty:    false,
          queuedId: null,
          serverPhotos:  actual?.photos || [],
          pendingPhotos: [],
        };
      });
      setEntries(init);
    } catch (e) {
      toast.error('Failed to load activities');
    }
    setLoading(false);
  }, [projectId, discFilter, date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Voice-to-text (Web Speech API) ──────────────────────────────────────
  const recogRef = useRef(null);
  const startVoice = (actId) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice input not supported on this device/browser'); return; }
    if (recogRef.current) { try { recogRef.current.stop(); } catch (_) {} }
    const r = new SR();
    r.lang = navigator.language || 'en-IN';
    r.continuous = false;
    r.interimResults = false;
    r.onresult = (e) => {
      const txt = Array.from(e.results).map(r => r[0].transcript).join(' ').trim();
      setEntries(prev => ({
        ...prev,
        [actId]: { ...prev[actId], remarks: (prev[actId]?.remarks ? prev[actId].remarks + ' ' : '') + txt, dirty: true },
      }));
    };
    r.onerror = (e) => { toast.error(`Voice: ${e.error || 'failed'}`); setVoiceFor(null); };
    r.onend   = () => setVoiceFor(null);
    setVoiceFor(actId);
    r.start();
    recogRef.current = r;
  };
  const stopVoice = () => {
    if (recogRef.current) { try { recogRef.current.stop(); } catch (_) {} }
    setVoiceFor(null);
  };

  const updateEntry = (actId, field, value) => {
    setEntries(prev => ({
      ...prev,
      [actId]: { ...prev[actId], [field]: value, dirty: true },
    }));
  };

  // ── Photo attachments ───────────────────────────────────────────────────
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const photoSrc = (url) => (url?.startsWith('http') ? url : `${BACKEND_URL}${url}`);

  const handlePhotoSelect = async (actId, fileList) => {
    if (!fileList || fileList.length === 0) return;
    const newPhotos = [];
    for (const f of Array.from(fileList)) {
      if (!f.type || !f.type.startsWith('image/')) continue;
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: exceeds 8MB limit`); continue; }
      newPhotos.push({
        id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        blob: f,
        filename: f.name || 'photo.jpg',
        mimeType: f.type || 'image/jpeg',
        previewUrl: URL.createObjectURL(f),
        uploading: false,
      });
    }
    if (newPhotos.length === 0) return;

    if (online) {
      setEntries(prev => ({
        ...prev,
        [actId]: { ...prev[actId], pendingPhotos: [...(prev[actId]?.pendingPhotos || []), ...newPhotos.map(p => ({ ...p, uploading: true }))] },
      }));
      for (const ph of newPhotos) {
        try {
          const fd = new FormData();
          fd.append('file', ph.blob, ph.filename);
          const res = await api.post(
            `/v1/projects/${projectId}/fc/activities/${actId}/progress/${date}/photos`,
            fd, { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          setEntries(prev => ({
            ...prev,
            [actId]: {
              ...prev[actId],
              serverPhotos: [...(prev[actId]?.serverPhotos || []), res.data],
              pendingPhotos: (prev[actId]?.pendingPhotos || []).filter(p => p.id !== ph.id),
            },
          }));
          URL.revokeObjectURL(ph.previewUrl);
        } catch {
          setEntries(prev => ({
            ...prev,
            [actId]: {
              ...prev[actId],
              pendingPhotos: (prev[actId]?.pendingPhotos || []).map(p =>
                p.id === ph.id ? { ...p, uploading: false } : p
              ),
            },
          }));
          if (navigator.onLine) toast.error(`Upload failed: ${ph.filename}`);
        }
      }
      toast.success(`${newPhotos.length} photo${newPhotos.length > 1 ? 's' : ''} attached`);
    } else {
      setEntries(prev => ({
        ...prev,
        [actId]: { ...prev[actId], pendingPhotos: [...(prev[actId]?.pendingPhotos || []), ...newPhotos], dirty: true },
      }));
      toast.info(`${newPhotos.length} photo${newPhotos.length > 1 ? 's' : ''} queued — will upload on reconnect`);
    }
  };

  const deleteServerPhoto = async (actId, filename) => {
    try {
      await api.delete(`/v1/projects/${projectId}/fc/activities/${actId}/progress/${date}/photos/${filename}`);
      setEntries(prev => ({
        ...prev,
        [actId]: { ...prev[actId], serverPhotos: (prev[actId]?.serverPhotos || []).filter(p => p.filename !== filename) },
      }));
    } catch { toast.error('Failed to delete photo'); }
  };

  const removePendingPhoto = (actId, localId) => {
    setEntries(prev => {
      const cur = prev[actId];
      if (!cur) return prev;
      const removed = (cur.pendingPhotos || []).find(p => p.id === localId);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return { ...prev, [actId]: { ...cur, pendingPhotos: (cur.pendingPhotos || []).filter(p => p.id !== localId) } };
    });
  };

  // ── Save individual / save all ──────────────────────────────────────────
  const saveOne = async (actId) => {
    const e = entries[actId];
    if (!e) return;
    const qty = Number(e.qty) || 0;
    const remarks = e.remarks || '';
    const pendingPhotos = e.pendingPhotos || [];
    if (!e.dirty && pendingPhotos.length === 0) return;
    try {
      if (online) {
        await api.put(
          `/v1/projects/${projectId}/fc/activities/${actId}/progress/${date}`,
          { mtdActualQty: qty, remarks }
        );
        setEntries(prev => ({ ...prev, [actId]: { ...prev[actId], dirty: false, queuedId: null } }));
        return { ok: true };
      } else {
        const queuedId = await dprQueue.enqueue({
          projectId, activityId: actId, periodId: date,
          mtdActualQty: qty, remarks,
          photos: pendingPhotos.map(p => ({ blob: p.blob, filename: p.filename, mimeType: p.mimeType })),
        });
        setEntries(prev => ({
          ...prev,
          [actId]: { ...prev[actId], dirty: false, queuedId, pendingPhotos: [] },
        }));
        return { ok: true, queued: true };
      }
    } catch {
      const queuedId = await dprQueue.enqueue({
        projectId, activityId: actId, periodId: date,
        mtdActualQty: qty, remarks,
        photos: pendingPhotos.map(p => ({ blob: p.blob, filename: p.filename, mimeType: p.mimeType })),
      });
      setEntries(prev => ({
        ...prev,
        [actId]: { ...prev[actId], dirty: false, queuedId, pendingPhotos: [] },
      }));
      return { ok: true, queued: true };
    }
  };

  const saveAll = async () => {
    setSaving(true);
    let ok = 0, queued = 0;
    for (const actId of Object.keys(entries)) {
      const e = entries[actId];
      if (!e?.dirty) continue;
      const res = await saveOne(actId);
      if (res?.ok) ok += 1;
      if (res?.queued) queued += 1;
    }
    setSaving(false);
    if (ok === 0) toast.info('No changes to save');
    else if (queued > 0) toast.success(`Saved ${ok} — ${queued} queued offline, will sync when back online`);
    else toast.success(`Saved ${ok} daily update${ok > 1 ? 's' : ''}`);
    if (online && queued === 0 && ok > 0) fetchData();
    refreshQueueCount();
  };

  const manualSync = async () => {
    if (!online) { toast.error('Still offline'); return; }
    const res = await dprQueue.sync(api);
    if (res.synced) toast.success(`Synced ${res.synced} queued`);
    if (res.failed) toast.error(`${res.failed} still pending`);
    refreshQueueCount();
    fetchData();
  };

  const shiftDay = (delta) => {
    const d = new Date(date + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + delta);
    setDate(fmtDate(d));
  };

  const dirtyCount = useMemo(
    () => Object.values(entries).filter(e => e?.dirty).length,
    [entries]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = activities;
    if (discFilter) rows = rows.filter(a => a.disciplineCode === discFilter);
    if (q) rows = rows.filter(a =>
      (a.activityName || '').toLowerCase().includes(q) ||
      (a.activityId || '').toLowerCase().includes(q) ||
      (a.area || '').toLowerCase().includes(q)
    );
    // Sort: dirty first, then in-flight before completed, then by activityId
    return rows.slice().sort((x, y) => {
      const dx = entries[x.id]?.dirty ? 0 : 1;
      const dy = entries[y.id]?.dirty ? 0 : 1;
      if (dx !== dy) return dx - dy;
      const cx = x.status === 'COMPLETED' ? 1 : 0;
      const cy = y.status === 'COMPLETED' ? 1 : 0;
      if (cx !== cy) return cx - cy;
      return (x.activityId || '').localeCompare(y.activityId || '');
    });
  }, [activities, discFilter, search, entries]);

  // Aggregate "progress captured today" — sum of Today's qty across dirty rows.
  const capturedToday = useMemo(() => {
    let count = 0;
    for (const e of Object.values(entries)) if (Number(e?.qty) > 0) count += 1;
    return count;
  }, [entries]);

  if (!projectId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6"
        style={{ background: P.pageBg, color: P.text }}>
        <div className="text-center" data-testid="dpr-no-project">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-70" />
          <p className="text-base mb-4">Select a project to capture DPR</p>
          <button onClick={() => navigate('/project-selector')}
            className="px-5 py-3 rounded-xl font-medium"
            style={{ background: P.accent, color: '#fff' }}>
            Select Project
          </button>
        </div>
      </div>
    );
  }

  const totalActive = activities.length;
  const totalCompleted = activities.filter(a => a.status === 'COMPLETED').length;

  return (
    <div className="min-h-screen pb-32" style={{ background: P.pageBg, color: P.text }} data-testid="dpr-capture-page">
      {/* ─── Sticky top bar ──────────────────────────────────────────── */}
      <div className="sticky top-0 z-30 backdrop-blur-md"
        style={{ background: P.stickyBg, borderBottom: `1px solid ${P.border}` }}>
        <div className="px-3 py-3 flex items-center gap-2">
          <button onClick={() => navigate('/project-controls/construction')}
            className="p-3 rounded-xl active:scale-95 transition"
            style={{ background: P.surfaceAlt, border: `1px solid ${P.border}`, minWidth: 44, minHeight: 44 }}
            data-testid="dpr-back-btn" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider" style={{ color: P.textMuted }}>Daily DPR Capture</div>
            <div className="text-sm font-semibold truncate">{selectedProject?.name || 'Project'}</div>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium"
            data-testid="dpr-connectivity-badge"
            style={{
              background: online ? '#10b98120' : '#ef444420',
              color: online ? '#10b981' : '#ef4444',
            }}>
            {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {online ? 'Online' : 'Offline'}
          </div>
        </div>

        {/* Date stepper */}
        <div className="px-3 pb-3 flex items-center gap-2">
          <button onClick={() => shiftDay(-1)} className="p-3 rounded-xl active:scale-95"
            style={{ background: P.surfaceAlt, border: `1px solid ${P.border}`, minWidth: 44, minHeight: 44 }}
            data-testid="dpr-prev-day-btn" aria-label="Previous day">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <label className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl"
            style={{ background: P.surfaceAlt, border: `1px solid ${P.border}`, minHeight: 44 }}>
            <CalendarDays className="w-5 h-5" style={{ color: P.textMuted }} />
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="bg-transparent flex-1 text-base font-medium outline-none"
              style={{ colorScheme: P.inputColorScheme, color: P.text }}
              data-testid="dpr-date-input"
            />
            <span className="text-xs" style={{ color: P.textMuted }}>{fmtLabel(new Date(date + 'T00:00:00Z'))}</span>
          </label>
          <button onClick={() => shiftDay(1)} className="p-3 rounded-xl active:scale-95"
            style={{ background: P.surfaceAlt, border: `1px solid ${P.border}`, minWidth: 44, minHeight: 44 }}
            data-testid="dpr-next-day-btn" aria-label="Next day">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-3 pb-3">
          <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
            style={{ background: P.surfaceAlt, border: `1px solid ${P.border}`, minHeight: 44 }}>
            <Search className="w-4 h-4" style={{ color: P.textMuted }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search activity, ID or area…"
              className="bg-transparent flex-1 text-sm outline-none"
              style={{ color: P.text }}
              data-testid="dpr-search-input"
            />
            {search && (
              <button onClick={() => setSearch('')} className="p-1 rounded-full" aria-label="Clear search">
                <X className="w-4 h-4" style={{ color: P.textMuted }} />
              </button>
            )}
          </label>
        </div>

        {/* Discipline filter chips */}
        {disciplines.length > 0 && (
          <div className="px-3 pb-3 flex items-center gap-2 overflow-x-auto"
            style={{ scrollbarWidth: 'none' }} data-testid="dpr-discipline-filter">
            <button onClick={() => setDiscFilter('')}
              className="px-3 py-1.5 rounded-full text-xs font-medium shrink-0 active:scale-95"
              style={{
                background: !discFilter ? P.accent : P.surfaceAlt,
                color: !discFilter ? '#fff' : P.textMuted,
                border: `1px solid ${!discFilter ? P.accent : P.border}`,
              }}>
              All
              <span className="ml-1.5 opacity-80">{totalActive}</span>
            </button>
            {disciplines.map(d => {
              const on = discFilter === d.disciplineCode;
              return (
                <button key={d.disciplineCode} onClick={() => setDiscFilter(d.disciplineCode)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium shrink-0 active:scale-95 whitespace-nowrap"
                  style={{
                    background: on ? d.displayColor : P.surfaceAlt,
                    color: on ? '#fff' : P.textMuted,
                    border: `1px solid ${on ? d.displayColor : P.border}`,
                    borderLeft: `3px solid ${d.displayColor}`,
                  }}>
                  {d.disciplineName}
                </button>
              );
            })}
          </div>
        )}

        {/* Progress bar strip */}
        <div className="px-3 pb-3">
          <div className="flex items-center justify-between text-[11px] mb-1.5" style={{ color: P.textMuted }}>
            <span className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> {capturedToday} of {totalActive} captured today</span>
            <span>{totalCompleted} completed · {totalActive - totalCompleted} in-flight</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: P.barTrack }}>
            <div className="h-full transition-all" style={{
              width: `${totalActive ? Math.min(100, (capturedToday / totalActive) * 100) : 0}%`,
              background: `linear-gradient(90deg, ${P.accent}, #10b981)`,
            }} />
          </div>
        </div>

        {/* Queue status banner */}
        {queueCount > 0 && (
          <div className="px-3 pb-3" data-testid="dpr-queue-banner">
            <button onClick={manualSync}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm active:scale-[0.99]"
              style={{ background: '#f59e0b18', color: '#b45309', border: '1px solid #f59e0b40' }}>
              <span className="flex items-center gap-2">
                <CloudOff className="w-4 h-4" />
                {queueCount} update{queueCount > 1 ? 's' : ''} queued offline
              </span>
              <span className="flex items-center gap-1 text-xs font-semibold">
                {online ? 'Tap to sync' : 'Waiting for connection'} <RefreshCw className="w-3.5 h-3.5" />
              </span>
            </button>
          </div>
        )}
      </div>

      {/* ─── Activity cards ──────────────────────────────────────────── */}
      <div className="px-3 py-4 space-y-3">
        {loading && (
          <div className="flex items-center justify-center py-12" data-testid="dpr-loading">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: P.textMuted }} />
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-16" data-testid="dpr-empty">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: P.textFaint }} />
            <p style={{ color: P.textMuted }}>No activities match the current filters</p>
            <p className="text-xs mt-1" style={{ color: P.textFaint }}>Try clearing the discipline chip or search.</p>
          </div>
        )}
        {!loading && filtered.map(a => {
          const entry = entries[a.id] || { qty: '', remarks: '', dirty: false };
          const todayPlan = (a.plannedDistribution || []).find(p => p.periodId === date);
          const planQty = todayPlan ? Number(todayPlan.mtdPlanQty) || 0 : 0;
          const cp = a.currentProgress || {};
          const isCompleted = a.status === 'COMPLETED';
          const statusColor = STATUS_COLOR[a.status] || '#64748b';
          const statusLabel = STATUS_LABEL[a.status] || a.status;
          const queued = entry.queuedId != null;
          const progressPct = Number(cp.ftmActualPct || 0);
          const remainingQty = cp.remainingQty ?? a.scopeQuantity;

          // Quick-fill options — only when there's meaningful data
          const quickFills = [];
          if (planQty > 0)  quickFills.push({ label: 'Match plan', value: planQty });
          if (remainingQty > 0) {
            quickFills.push({ label: '+25%',   value: +(remainingQty * 0.25).toFixed(2) });
            quickFills.push({ label: 'Finish', value: +remainingQty.toFixed(2) });
          }

          return (
            <div key={a.id}
              className="rounded-2xl p-4 transition-shadow"
              style={{
                background: isCompleted ? P.completedBg : P.surface,
                border: `1px solid ${entry.dirty ? P.accent : P.border}`,
                boxShadow: entry.dirty ? `0 4px 20px ${P.accent}22` : 'none',
                opacity: isCompleted ? 0.85 : 1,
              }}
              data-testid={`dpr-activity-card-${a.activityId}`}>
              {/* Header */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ background: statusColor + '20', color: statusColor }}>
                      {a.disciplineCode}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider" style={{ color: P.textFaint }}>{a.activityId}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ background: statusColor + '15', color: statusColor }}>
                      {statusLabel}
                    </span>
                    {isCompleted && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1"
                        style={{ background: '#22c55e20', color: '#16a34a' }}>
                        <Lock className="w-2.5 h-2.5" /> Read-only
                      </span>
                    )}
                    {queued && !isCompleted && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ background: '#f59e0b20', color: '#b45309' }}>
                        Queued
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-semibold leading-tight" style={{ color: P.text }}>{a.activityName}</div>
                  {a.area && <div className="text-xs mt-0.5" style={{ color: P.textMuted }}>{a.area}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase" style={{ color: P.textFaint }}>Progress</div>
                  <div className="text-lg font-extrabold tabular-nums" style={{ color: statusColor }}>
                    {progressPct.toFixed(1)}%
                  </div>
                </div>
              </div>

              {/* Progress bar — mirrors desktop Wt% cell */}
              <div className="h-1.5 rounded-full overflow-hidden mb-3" style={{ background: P.barTrack }}>
                <div className="h-full transition-all" style={{
                  width: `${Math.max(2, Math.min(100, progressPct))}%`,
                  background: statusColor,
                }} />
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                <div className="rounded-lg p-2" style={{ background: P.inset }}>
                  <div className="text-[10px] uppercase" style={{ color: P.textFaint }}>Scope</div>
                  <div className="font-semibold tabular-nums" style={{ color: P.text }}>{a.scopeQuantity} {a.uom}</div>
                </div>
                <div className="rounded-lg p-2" style={{ background: P.inset }}>
                  <div className="text-[10px] uppercase" style={{ color: P.textFaint }}>Today plan</div>
                  <div className="font-semibold tabular-nums" style={{ color: P.text }}>{todayPlan ? todayPlan.mtdPlanQty : '—'} {a.uom}</div>
                </div>
                <div className="rounded-lg p-2" style={{ background: P.inset }}>
                  <div className="text-[10px] uppercase" style={{ color: P.textFaint }}>Remaining</div>
                  <div className="font-semibold tabular-nums" style={{ color: P.text }}>{remainingQty} {a.uom}</div>
                </div>
              </div>

              {/* Qty input — LARGE */}
              <label className="block">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] uppercase tracking-wider" style={{ color: P.textMuted }}>
                    Today's actual qty ({a.uom})
                  </span>
                  {!isCompleted && quickFills.length > 0 && (
                    <div className="flex items-center gap-1">
                      {quickFills.map(qf => (
                        <button key={qf.label} type="button"
                          onClick={() => updateEntry(a.id, 'qty', qf.value)}
                          className="px-2 py-1 rounded-md text-[10px] font-semibold active:scale-95"
                          style={{ background: P.accentSoft, color: P.accent, border: `1px solid ${P.accent}30` }}
                          data-testid={`dpr-quickfill-${a.activityId}-${qf.label.replace(/\W/g, '')}`}>
                          {qf.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="number"
                  inputMode="decimal"
                  step="any"
                  value={entry.qty}
                  onChange={e => updateEntry(a.id, 'qty', e.target.value)}
                  placeholder={isCompleted ? '—' : '0'}
                  disabled={isCompleted}
                  className="w-full text-2xl font-bold rounded-xl px-4 py-3.5 outline-none tabular-nums"
                  style={{
                    background: P.inset,
                    border: `1.5px solid ${entry.dirty ? P.accent : P.border}`,
                    color: P.text,
                    minHeight: 60,
                    opacity: isCompleted ? 0.55 : 1,
                    cursor: isCompleted ? 'not-allowed' : 'text',
                  }}
                  data-testid={`dpr-qty-input-${a.activityId}`}
                />
              </label>

              {/* Remarks + voice */}
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] uppercase tracking-wider" style={{ color: P.textMuted }}>Remarks</span>
                  {!isCompleted && (
                    <button onClick={() => (voiceFor === a.id ? stopVoice() : startVoice(a.id))}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium active:scale-95"
                      style={{
                        background: voiceFor === a.id ? '#ef444420' : P.accentSoft,
                        color: voiceFor === a.id ? '#ef4444' : P.accent,
                      }}
                      data-testid={`dpr-voice-btn-${a.activityId}`}>
                      {voiceFor === a.id ? <><MicOff className="w-3.5 h-3.5" /> Stop</> : <><Mic className="w-3.5 h-3.5" /> Voice</>}
                    </button>
                  )}
                </div>
                <textarea
                  value={entry.remarks}
                  onChange={e => updateEntry(a.id, 'remarks', e.target.value)}
                  placeholder="Optional — e.g. weather, manpower notes…"
                  disabled={isCompleted}
                  rows={2}
                  className="w-full rounded-xl px-3 py-2.5 outline-none text-sm resize-none"
                  style={{
                    background: P.inset,
                    border: `1px solid ${P.border}`,
                    color: P.text,
                    opacity: isCompleted ? 0.55 : 1,
                  }}
                  data-testid={`dpr-remarks-input-${a.activityId}`}
                />
              </div>

              {/* Photo evidence */}
              {!isCompleted && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] uppercase tracking-wider" style={{ color: P.textMuted }}>
                      Photo evidence
                      {((entry.serverPhotos?.length || 0) + (entry.pendingPhotos?.length || 0)) > 0 && (
                        <span className="ml-1.5" style={{ color: P.text }}>
                          ({(entry.serverPhotos?.length || 0) + (entry.pendingPhotos?.length || 0)})
                        </span>
                      )}
                    </span>
                    <label
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium active:scale-95 cursor-pointer"
                      style={{ background: '#10b98120', color: '#059669' }}
                      data-testid={`dpr-add-photo-btn-${a.activityId}`}
                    >
                      <Camera className="w-3.5 h-3.5" /> Add Photo
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        hidden
                        onChange={e => { handlePhotoSelect(a.id, e.target.files); e.target.value = ''; }}
                      />
                    </label>
                  </div>
                  {((entry.serverPhotos?.length || 0) + (entry.pendingPhotos?.length || 0)) === 0 ? (
                    <label className="block w-full rounded-xl border-2 border-dashed py-4 px-3 text-center cursor-pointer active:scale-[0.99]"
                      style={{ borderColor: P.border, color: P.textFaint }}
                      data-testid={`dpr-photo-dropzone-${a.activityId}`}>
                      <ImagePlus className="w-5 h-5 mx-auto mb-1" />
                      <div className="text-xs">Tap to attach evidence photos</div>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        hidden
                        onChange={e => { handlePhotoSelect(a.id, e.target.files); e.target.value = ''; }}
                      />
                    </label>
                  ) : (
                    <div className="grid grid-cols-3 gap-2" data-testid={`dpr-photo-grid-${a.activityId}`}>
                      {(entry.serverPhotos || []).map(p => (
                        <div key={p.filename} className="relative aspect-square rounded-lg overflow-hidden"
                          style={{ background: P.inset, border: `1px solid ${P.border}` }}>
                          <img src={photoSrc(p.url)} alt={p.originalName || 'evidence'}
                            className="w-full h-full object-cover" loading="lazy" />
                          <button onClick={() => deleteServerPhoto(a.id, p.filename)}
                            className="absolute top-1 right-1 p-1 rounded-full active:scale-90"
                            style={{ background: 'rgba(0,0,0,0.65)', color: '#fff' }}
                            aria-label="Delete photo"
                            data-testid={`dpr-photo-delete-${a.activityId}-${p.filename}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {(entry.pendingPhotos || []).map(p => (
                        <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden"
                          style={{ background: P.inset, border: '1px dashed #f59e0b' }}>
                          <img src={p.previewUrl} alt={p.filename || 'pending'}
                            className="w-full h-full object-cover opacity-90" />
                          <div className="absolute inset-x-0 bottom-0 px-1.5 py-0.5 text-[9px] uppercase font-semibold"
                            style={{ background: 'rgba(245,158,11,0.85)', color: '#000' }}>
                            {p.uploading ? 'Uploading…' : 'Queued'}
                          </div>
                          <button onClick={() => removePendingPhoto(a.id, p.id)}
                            className="absolute top-1 right-1 p-1 rounded-full active:scale-90"
                            style={{ background: 'rgba(0,0,0,0.65)', color: '#fff' }}
                            aria-label="Remove pending photo">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Crew & plant for the day */}
              <button onClick={() => setReturnFor({ id: a.id, activityId: a.activityId, activityName: a.activityName, uom: a.uom })}
                className="w-full mt-3 py-2.5 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition"
                style={{ background: P.inset, color: P.text, border: `1px solid ${P.border}`, minHeight: 44 }}
                data-testid={`dpr-crew-plant-btn-${a.activityId}`}>
                <HardHat className="w-4 h-4" />
                Crew &amp; plant
              </button>

              {/* Per-card save */}
              {!isCompleted && (
                <button onClick={() => saveOne(a.id).then(r => {
                  if (r?.ok && !r.queued) toast.success('Saved');
                  else if (r?.ok && r.queued) toast.info('Queued offline');
                })}
                  disabled={!entry.dirty && (entry.pendingPhotos?.length || 0) === 0}
                  className="w-full mt-3 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition disabled:opacity-40"
                  style={{
                    background: (entry.dirty || (entry.pendingPhotos?.length || 0) > 0) ? P.accent : P.inset,
                    color: (entry.dirty || (entry.pendingPhotos?.length || 0) > 0) ? '#fff' : P.textFaint,
                    border: (entry.dirty || (entry.pendingPhotos?.length || 0) > 0) ? 'none' : `1px solid ${P.border}`,
                    minHeight: 48,
                  }}
                  data-testid={`dpr-save-btn-${a.activityId}`}>
                  <Save className="w-4 h-4" />
                  {entry.dirty || (entry.pendingPhotos?.length || 0) > 0 ? (online ? 'Save' : 'Queue offline') : 'Saved'}
                </button>
              )}
            </div>
          );
        })}

        {!loading && filtered.length > 0 && (
          <div className="pt-2 pb-4 text-center text-[11px]" style={{ color: P.textFaint }}>
            <Sparkles className="w-3.5 h-3.5 inline mr-1" />
            Showing {filtered.length} of {totalActive} · {fmtLong(new Date(date + 'T00:00:00Z'))}
          </div>
        )}
      </div>

      <SiteReturnSheet
        open={!!returnFor}
        onOpenChange={(o) => { if (!o) setReturnFor(null); }}
        projectId={projectId}
        activity={returnFor}
        date={date}
      />

      {/* ─── Sticky save-all bottom bar ──────────────────────────────────── */}
      {dirtyCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 backdrop-blur-md p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
          style={{ background: P.stickyBg, borderTop: `1px solid ${P.border}` }}>
          <button onClick={saveAll} disabled={saving}
            className="w-full py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2 active:scale-[0.99] disabled:opacity-60"
            style={{ background: online ? P.accent : '#f59e0b', color: '#fff', minHeight: 56 }}
            data-testid="dpr-save-all-btn">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (online ? <CloudUpload className="w-5 h-5" /> : <CloudOff className="w-5 h-5" />)}
            {online
              ? `Submit ${dirtyCount} update${dirtyCount > 1 ? 's' : ''}`
              : `Queue ${dirtyCount} offline`}
          </button>
        </div>
      )}
    </div>
  );
}
